import React, { useState, useMemo, useRef } from 'react';
import {
    View, Text, StyleSheet, TextInput, SectionList,
    TouchableOpacity, ActivityIndicator, Platform, Keyboard,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { searchUsers } from '../../../api/social';
import { getOrCreateDirectRoom } from '../../../api/chat';
import UserAvatar from '../../../components/social/shared/UserAvatar';

/* ── Alphabetical clustering ─────────────────────────────────────── */
const groupByLetter = (users) => {
    const map = {};
    users.forEach((user) => {
        const first = (user.name || '?')[0].toUpperCase();
        const key = /^[A-Z]$/.test(first) ? first : '#';
        if (!map[key]) map[key] = [];
        map[key].push(user);
    });
    return Object.keys(map)
        .sort()
        .map((letter) => ({ title: letter, data: map[letter] }));
};

/* ═══════════════════════════════════════════════════════════════════
   Screen
═══════════════════════════════════════════════════════════════════ */
const NewChatScreen = ({ navigation }) => {
    const insets = useSafeAreaInsets();
    const searchRef = useRef(null);
    const [query, setQuery] = useState('');
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);

    React.useEffect(() => { handleSearch(''); }, []);

    const handleSearch = async (text) => {
        setQuery(text);
        if (text.length > 0 && text.length < 2) return;
        setLoading(true);
        try {
            const data = await searchUsers(text);
            setUsers(data);
        } catch (e) {
            console.error('Search error:', e);
        } finally {
            setLoading(false);
        }
    };

    const startChat = async (opponent) => {
        Keyboard.dismiss();
        try {
            const room = await getOrCreateDirectRoom(opponent.id);
            navigation.replace('ChatRoom', { roomId: room.id, otherUser: opponent });
        } catch (e) {
            console.error('Start chat error:', e);
        }
    };

    const sections = useMemo(() => groupByLetter(users), [users]);

    /* ── Renderers ─────────────────────────────────────────────── */

    const renderSectionHeader = ({ section: { title } }) => (
        <View style={styles.sectionHeader}>
            <Text style={styles.sectionHeaderText}>{title}</Text>
        </View>
    );

    // Each item within a section gets a small top gap so groups feel spaced
    const renderItem = ({ item, index: itemIndex, section }) => {
        const isFirst = itemIndex === 0;
        return (
            <TouchableOpacity
                style={[styles.userItem, isFirst && styles.userItemFirst]}
                onPress={() => startChat(item)}
                activeOpacity={0.7}
            >
                <UserAvatar source={item.profileImageUrl} name={item.name} size={36} />
                <View style={styles.userInfo}>
                    <Text style={styles.userName} numberOfLines={1}>{item.name}</Text>
                    <View style={styles.statusRow}>
                        <View style={[
                            styles.statusDot,
                            { backgroundColor: item.isOnline ? '#10B981' : '#D1D5DB' },
                        ]} />
                        <Text style={[
                            styles.userStatus,
                            { color: item.isOnline ? '#10B981' : '#9CA3AF' },
                        ]}>
                            {item.isOnline ? 'Online' : 'Offline'}
                        </Text>
                    </View>
                </View>
                <Ionicons name="chatbubble-outline" size={15} color="#06b6d4" style={styles.chatIcon} />
            </TouchableOpacity>
        );
    };

    const ListEmpty = () => (
        <View style={styles.emptyContainer}>
            <Text style={styles.emptyIcon}>{query.length >= 2 ? '🔍' : '👥'}</Text>
            <Text style={styles.emptyTitle}>
                {query.length >= 2 ? 'No users found' : 'Find someone to chat'}
            </Text>
            <Text style={styles.emptyText}>
                {query.length >= 2
                    ? 'Try a different name or email'
                    : 'Type a name or email above to search'}
            </Text>
        </View>
    );

    /* ── JSX ───────────────────────────────────────────────────── */
    return (
        <View style={styles.container}>

            {/* ── Gradient header ───────────────────────────────── */}
            <View style={[styles.headerWrapper, { paddingTop: insets.top }]}>
                <LinearGradient
                    colors={['#0c4a6e', '#0369a1', '#0891b2']}
                    start={{ x: 0, y: 0 }}
                    end={{ x: 1, y: 1 }}
                    style={StyleSheet.absoluteFill}
                />
                <View style={styles.headerContent}>
                    {/* Back */}
                    <TouchableOpacity
                        style={styles.backBtn}
                        onPress={() => navigation.goBack()}
                        hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                    >
                        <Ionicons name="arrow-back" size={19} color="rgba(255,255,255,0.9)" />
                    </TouchableOpacity>

                    {/* Title */}
                    <Text style={styles.headerTitle}>New Message</Text>

                    {/* Badge icon */}
                    <View style={styles.headerBadge}>
                        <Ionicons name="chatbubble-ellipses" size={15} color="#22d3ee" />
                    </View>
                </View>

                {/* Search bar embedded in gradient */}
                <View style={styles.searchWrapper}>
                    <Ionicons name="search" size={14} color="rgba(255,255,255,0.6)" />
                    <TextInput
                        ref={searchRef}
                        style={styles.input}
                        placeholder="Search by name or email…"
                        placeholderTextColor="rgba(255,255,255,0.45)"
                        value={query}
                        onChangeText={handleSearch}
                        returnKeyType="search"
                        selectionColor="#22d3ee"
                    // No autoFocus — keyboard only opens on tap
                    />
                    {query.length > 0 && (
                        <TouchableOpacity
                            onPress={() => handleSearch('')}
                            hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                        >
                            <Ionicons name="close-circle" size={14} color="rgba(255,255,255,0.5)" />
                        </TouchableOpacity>
                    )}
                </View>
            </View>

            {/* ── Contact list ──────────────────────────────────── */}
            {loading ? (
                <View style={styles.loaderContainer}>
                    <ActivityIndicator color="#06b6d4" size="small" />
                    <Text style={styles.loaderText}>Searching…</Text>
                </View>
            ) : (
                <SectionList
                    sections={sections}
                    keyExtractor={(item) => item.id.toString()}
                    renderItem={renderItem}
                    renderSectionHeader={renderSectionHeader}
                    ListEmptyComponent={<ListEmpty />}
                    contentContainerStyle={styles.listContent}
                    stickySectionHeadersEnabled
                    keyboardShouldPersistTaps="handled"
                    keyboardDismissMode="on-drag"
                />
            )}
        </View>
    );
};

/* ═══════════════════════════════════════════════════════════════════
   Styles
═══════════════════════════════════════════════════════════════════ */
const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: '#F1F5F9',
    },

    /* ── Gradient header ── */
    headerWrapper: {
        overflow: 'hidden',
        paddingBottom: 10,
    },
    headerContent: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingHorizontal: 14,
        paddingTop: Platform.OS === 'ios' ? 8 : 14,
        paddingBottom: 10,
        gap: 10,
    },
    backBtn: {
        width: 32,
        height: 32,
        borderRadius: 9,
        backgroundColor: 'rgba(255,255,255,0.12)',
        alignItems: 'center',
        justifyContent: 'center',
    },
    headerTitle: {
        flex: 1,
        fontSize: 16,
        fontWeight: '800',
        color: '#FFFFFF',
        letterSpacing: 0.2,
    },
    headerBadge: {
        width: 32,
        height: 32,
        borderRadius: 9,
        backgroundColor: 'rgba(34,211,238,0.18)',
        borderWidth: 1,
        borderColor: 'rgba(34,211,238,0.3)',
        alignItems: 'center',
        justifyContent: 'center',
    },

    /* Search bar inside gradient */
    searchWrapper: {
        flexDirection: 'row',
        alignItems: 'center',
        marginHorizontal: 14,
        backgroundColor: 'rgba(255,255,255,0.13)',
        borderRadius: 10,
        borderWidth: 1,
        borderColor: 'rgba(255,255,255,0.2)',
        paddingHorizontal: 10,
        paddingVertical: Platform.OS === 'ios' ? 7 : 5,
        gap: 7,
    },
    input: {
        flex: 1,
        fontSize: 13,
        color: '#FFFFFF',
        paddingVertical: 0,
    },

    /* Section header — letter cluster */
    sectionHeader: {
        backgroundColor: '#F8FAFC',
        paddingHorizontal: 16,
        paddingVertical: 6,
        borderTopWidth: 1,
        borderTopColor: '#E2E8F0',
        borderBottomWidth: 1,
        borderBottomColor: '#E2E8F0',
    },
    sectionHeaderText: {
        fontSize: 10,
        fontWeight: '700',
        color: '#64748b',
        letterSpacing: 1.2,
        textTransform: 'uppercase',
    },

    /* List */
    listContent: {
        paddingBottom: 24,
    },
    loaderContainer: {
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'row',
        gap: 8,
    },
    loaderText: {
        color: '#06b6d4',
        fontSize: 13,
        fontWeight: '500',
    },

    /* Contact row */
    userItem: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: '#FFFFFF',
        paddingHorizontal: 14,
        paddingVertical: 8,
        // hairline separator between rows in the same group
        borderBottomWidth: StyleSheet.hairlineWidth,
        borderBottomColor: '#E9EFF5',
        gap: 10,
    },
    // First item in a section gets a tiny top margin to visually
    // separate groups from each other (the section header already helps,
    // but this adds breathing room used as "light spacing between groups")
    userItemFirst: {
        marginTop: 0,
    },
    userInfo: {
        flex: 1,
    },
    userName: {
        fontSize: 13,
        fontWeight: '600',
        color: '#0f172a',
        marginBottom: 1,
    },
    statusRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
    },
    statusDot: {
        width: 5,
        height: 5,
        borderRadius: 3,
    },
    userStatus: {
        fontSize: 10,
        fontWeight: '500',
    },
    chatIcon: {
        opacity: 0.65,
    },

    /* Empty state */
    emptyContainer: {
        alignItems: 'center',
        paddingTop: 56,
        paddingHorizontal: 24,
    },
    emptyIcon: { fontSize: 34, marginBottom: 10 },
    emptyTitle: { fontSize: 15, fontWeight: '700', color: '#374151', marginBottom: 4 },
    emptyText: { fontSize: 13, color: '#9CA3AF', textAlign: 'center', lineHeight: 18 },
});

export default NewChatScreen;
